import React, { useCallback } from "react";
import { useNotify, usePermissions, useTranslate } from "react-admin";
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
  Tooltip,
  useTheme,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";

import { InfoBox } from "./InfoBox";
import { FileUploader } from "./FileUploader";
import { UserRole } from "../../../entities";
import { ConfigFileStatus } from "../../../entities/ConfigFileStatus";
import { useConfigFileUploader } from "../hooks/useConfigFileUploader";
import { useConfigFileLoader } from "../hooks/useConfigFileLoader";
import { ConfigFile, useTerminalContext } from "../contexts/TerminalContext";

interface ConfigFileManagerProps {
  terminalId: number | undefined;
  fileList: ConfigFile[];
  selectedSrc: string;
  onSrcChange: (src: string) => void;
}

export const ConfigFileManager: React.FC<ConfigFileManagerProps> = ({
  terminalId,
  fileList,
  selectedSrc,
  onSrcChange,
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const { permissions } = usePermissions();
  const { uploadConfigFile } = useConfigFileUploader();
  const { loadConfigFile } = useConfigFileLoader();

  const isAdmin = permissions && permissions.role === UserRole.admin;
  const { isLoading } = useTerminalContext();

  // Handler to load a different config file when selected
  const handleConfigFileChange = useCallback(
    async (src: string) => {
      if (!terminalId) return;
      try {
        onSrcChange(src);
        const selectedFile = fileList.find((f) => f.src === src);
        await loadConfigFile(terminalId, src, selectedFile?.status || null);
      } catch (error: any) {
        notify(translate("osfc.storage_ui.config_files.load_failed", { error: error.message }), "error");
      }
    },
    [terminalId, fileList, loadConfigFile, notify, onSrcChange]
  );

  const handleUploadAsDraft = async (file: File) => {
    if (!terminalId) {
      notify("osfc.storage_ui.config_files.select_terminal", "warning");
      return;
    }

    try {
      await uploadConfigFile(terminalId, file, ConfigFileStatus.draft);
      notify("osfc.storage_ui.config_files.uploaded_draft", "success");
    } catch (error: any) {
      notify(error.message, "error");
      throw error;
    }
  };

  const handleUploadToProduction = async (file: File) => {
    if (!terminalId) {
      notify("osfc.storage_ui.config_files.select_terminal", "warning");
      return;
    }

    try {
      await uploadConfigFile(
        terminalId,
        file,
        ConfigFileStatus.production
      );
      notify("osfc.storage_ui.config_files.uploaded_production", "success");
    } catch (error: any) {
      notify(error.message, "error");
      throw error;
    }
  };

  const downloadConfigFile = useCallback(
    (file: ConfigFile) => {
      const url = file.preview_src;
      if (!url) {
        notify("osfc.storage_ui.config_files.download_missing", "warning");
        return;
      }
      window.open(url, "_blank");
    },
    [notify]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: ConfigFileStatus) => {
    return status === ConfigFileStatus.production
      ? theme.palette.storageUi.configProduction
      : theme.palette.storageUi.configDraft;
  };

  const getStatusLabel = (status: ConfigFileStatus) => {
    return status === ConfigFileStatus.production ? translate("osfc.storage_ui.config_files.production") : translate("osfc.storage_ui.config_files.draft");
  };

  // Sort files: production first, then by updated_at descending
  const sortedFileList = [...fileList].sort((a, b) => {
    // Production always first
    if (a.status === ConfigFileStatus.production && b.status !== ConfigFileStatus.production) {
      return -1;
    }
    if (a.status !== ConfigFileStatus.production && b.status === ConfigFileStatus.production) {
      return 1;
    }
    // Otherwise sort by updated_at descending (newest first)
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  return (
    <>
      {isAdmin && (
        <FileUploader
          title={translate("osfc.storage_ui.config_files.upload_title")}
          accept=".json"
          actions={[
            {
              label: translate("osfc.storage_ui.config_files.upload_draft"),
              onClick: handleUploadAsDraft,
              color: "primary",
            },
            {
              label: translate("osfc.storage_ui.config_files.upload_production"),
              onClick: handleUploadToProduction,
              color: "secondary",
            },
          ]}
          isLoading={isLoading}
          style={{ marginBottom: "15px" }}
        />
      )}
      {fileList.length > 0 && (
        <InfoBox title={translate("osfc.storage_ui.config_files.title")} style={{ marginBottom: "15px" }}>
          {isLoading && fileList.length === 0 ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <CircularProgress size={20} />
            </div>
          ) : (
          <div style={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontSize: "10px", padding: "4px 8px", width: "30px" }}></TableCell>
                  <TableCell style={{ fontSize: "10px", padding: "4px 8px", width: "30px" }}></TableCell>
                  <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                    {translate("osfc.storage_ui.config_files.filename")}
                  </TableCell>
                  <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                    {translate("osfc.storage_ui.config_files.updated")}
                  </TableCell>
                  <TableCell style={{ fontSize: "10px", padding: "4px 8px", width: "70px" }}>
                    {translate("osfc.storage_ui.config_files.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFileList.map((file) => (
                  <TableRow
                    key={file.src}
                    hover
                    onClick={() => handleConfigFileChange(file.src)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                      <Radio
                        checked={selectedSrc === file.src}
                        onChange={() => handleConfigFileChange(file.src)}
                        value={file.src}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                      <Tooltip title={getStatusLabel(file.status)} arrow>
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: getStatusColor(file.status),
                            borderRadius: "3px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                      {file.name}
                    </TableCell>
                    <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                      {formatDate(file.updated_at)}
                    </TableCell>
                    <TableCell style={{ fontSize: "10px", padding: "4px 8px" }}>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadConfigFile(file);
                        }}
                        size="small"
                        variant="text"
                        startIcon={<GetAppIcon style={{ fontSize: "14px" }} />}
                        style={{
                          textTransform: "none",
                          fontSize: "10px",
                          padding: 0,
                        }}
                      >
                        {translate("osfc.storage_ui.config_files.download")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </InfoBox>
      )}
    </>
  );
};
